<template>
  <router-link
    v-if="to"
    :class="componentClasses"
    :to="to"
    :replace="replace"
  >
    <Icon v-if="iconOnly" :icon="iconOnly" />
    <template v-else>
      <Icon v-if="iconBefore" :icon="iconBefore" size="3" />
      <span class="stk-badge-body">
        <slot />
      </span>
      <Icon v-if="iconAfter" :icon="iconAfter" />
    </template>
  </router-link>
  <a v-else-if="href" :class="componentClasses">
    <Icon v-if="iconOnly" :icon="iconOnly" />
    <template v-else>
      <Icon
        v-if="iconBefore"
        :icon="iconBefore"
        size="3"
      />
      <span class="stk-badge-body">
        <slot />
      </span>
      <Icon
        v-if="iconAfter"
        :icon="iconAfter"
      />
    </template>
  </a>
  <span
    v-else
    :class="componentClasses"
    @click="$emit('click', $event)"
  >
    <Icon v-if="iconOnly" :icon="iconOnly" />
    <template v-else>
      <Icon
        v-if="iconBefore"
        :icon="iconBefore"
        size="3"
      />
      <span class="stk-badge-body">
        <slot />
      </span>
      <Icon
        v-if="iconAfter"
        :icon="iconAfter"
      />
    </template>
  </span>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@stakeui/graphic/icon/Icon.vue';

const props = defineProps({
  status: String,
  href: String,
  iconBefore: String,
  iconAfter: String,
  iconOnly: String,
  priority: String,
  heightOnly: Boolean,
  clickable: Boolean,
  outlined: Boolean,
  replace: Boolean,
  resizable: Boolean,
  bold: Boolean,
  to: Object,
});

const componentClasses = computed(() => [
  'stk-badge',
  {
    [`is-${props.status}`]: props.status,
    [`priority-${props.priority}`]: props.priority,
    [`is-link`]: props.href,
    [`is-height-only`]: props.heightOnly,
    [`is-clickable`]: props.clickable,
    [`is-outlined`]: props.outlined,
    [`is-resizable`]: props.resizable,
    [`is-bold`]: props.bold,
    ['is-icon-only']: props.iconOnly,
  },
]);
</script>

<style>
.stk-badge {
  --background-color: var(--stk-text-95);
  --color: var(--stk-text-30);
  background-color: var(--background-color);
  color: var(--color);
  display: inline-flex;
  vertical-align: top;
  align-items: center;
  justify-content: start;
  line-height: 1rem;
  padding: 0.25em 0.375em;
  border-radius: 0.5em;
  gap: 0.125em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;

  strong {
    color: inherit;
  }

  &.is-clickable {
    cursor: pointer;
  }

  &.is-icon-only {
    padding: 0.25em;

    .stk-icon {
      height: 1rem;
      font-size: 0.75em;
      margin: 0;
      width: 1rem;
    }
  }

  &.is-height-only {
    padding-left: 0;
    padding-right: 0;
  }

  &.is-outlined {
    outline-width: 1px;
    outline-color: currentColor;
    outline-style: solid;
  }

  &.is-primary {
    --background-color: var(--stk-primary-90);
    --color: var(--stk-primary-30);

    &.is-outlined {
      --background-color: var(--stk-primary-95);
      --color: var(--stk-primary-50);
    }

    &.is-bold {
      --color: var(--stk-primary-90);
      --background-color: var(--stk-primary-30);
    }
  }

  &.is-success {
    --background-color: var(--stk-success-90);
    --color: var(--stk-success-30);

    &.is-bold {
      --color: var(--stk-success-90);
      --background-color: var(--stk-success-30);
    }
  }

  &.is-info {
    --background-color: var(--stk-info-95);
    --color: var(--stk-info-45);

    &.is-bold {
      --color: var(--stk-info-95);
      --background-color: var(--stk-info-30);
    }
  }

  &.is-warning {
    --background-color: var(--stk-warning-90);
    --color: var(--stk-warning-30);

    &.is-bold {
      --color: var(--stk-warning-90);
      --background-color: var(--stk-warning-30);
    }
  }

  &.is-danger {
    --background-color: var(--stk-danger-90);
    --color: var(--stk-danger-30);

    &.is-bold {
      --color: var(--stk-danger-90);
      --background-color: var(--stk-danger-30);
    }
  }

  &.is-ask-anything {
    --background-color: hsl(var(--stk-ai-pink-h) var(--stk-ai-pink-s) 97%);
    --color: var(--stk-ai-pink);

    &:hover {
      --background-color: hsl(var(--stk-ai-pink-h) var(--stk-ai-pink-s) 95%);
    }
  }

  &.is-future {
    --background-color: var(--stk-future-90);
    --color: var(--stk-future-30);
  }

  &.is-disabled {
    --background-color: var(--stk-background);
    --color: var(--stk-text-weak);
  }

  &.is-ghost {
    --background-color: transparent;
    color: inherit;
  }

  &.is-link {
    --background-color: transparent;
    padding-left: 0;
    padding-right: 0;

    &:hover {
      color: var(--stk-primary);
      text-decoration: underline;
    }
  }

  &.is-resizable {
    line-height: 1;

    .stk-badge-body {
      font-size: 0.875em;
    }
  }

  &.priority-critical {
    background-color: var(--stk-danger-50);
    color: var(--stk-danger-90);
  }

  &.priority-high {
    background-color: var(--stk-warning-95);
    color: var(--stk-warning-50);
  }

  &.priority-medium {
    background-color: var(--stk-info-95);
    color: var(--stk-info-60);
  }

  &.priority-low {
    background-color: var(--stk-success-95);
    color: var(--stk-success-50);
  }

  .stk-icon {
    margin: -0.25em -0.125em;
  }
}

.stk-badge-body {
  font-size: 0.875rem;
  font-weight: 500;
}
</style>
